import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { DetroitGameState, GoodsPlacementInfo } from '../../types';
import { placeGoodsFromDisplay } from './refillGoodsPhase';

/**
 * Phase 9: 商品補充フェーズ（Detroit Bankruptcy Solo専用）
 *
 * Detroit固有ロジック:
 * - ライトサイド用のダイス2個、ダークサイド用のダイス2個（計4回）
 * - 各ダイス目に応じた列の商品が都市に配置される
 *
 * 参照:
 * - docs/specifications/logic/detroit_bankruptcy_solo/phase_09_goods_growth.md
 */
export const refillGoodsDetroitPhase: PhaseConfig<DetroitGameState> = {
  onBegin: ({ G, random }: { G: DetroitGameState; random: any }) => {
    debug.log('[refillGoodsDetroitPhase] ★★★ Phase 9: Refill Goods started ★★★');

    // Detroit: ライトサイドとダークサイドで各2個ずつダイスを振る（計4回）
    const { diceRolls, placements } = executeDiceRollRefillDetroit(G, random);

    // ML学習用: 自動フェーズのランダム結果を記録
    G.moveLog.push({
      turn: (G as any).actualTurn || 0,
      phase: 'refillGoods',
      type: 'autoPhase',
      randomResults: { diceRolls, placements },
    });

    // 結果をゲーム状態に保存
    (G as any).lastDiceRolls = diceRolls;
    (G as any).lastGoodsPlacement = placements;

    // UI表示用
    const placementText = placements.length > 0
      ? placements.map(p => `${p.color} → ${p.cityName}`).join(', ')
      : 'No goods placed';

    G.autoPhaseResult = {
      phase: 'refillGoods',
      message: `Light: ${diceRolls.light.join(',')} Dark: ${diceRolls.dark.join(',')}. ${placementText}`,
      floatingText: `🎲 L:${diceRolls.light.join(',')} D:${diceRolls.dark.join(',')}`,
      details: { diceRolls, placements },
    };

    (G as any).refillGoodsStarted = true;
    debug.log('[refillGoodsDetroitPhase] ★ Waiting for user click to proceed');
  },

  moves: {
    proceedAutoPhase: ({ G }: { G: DetroitGameState }) => {
      G.autoPhaseResult = null;
    },
  },

  endIf: ({ G }: { G: DetroitGameState }) => {
    const started = (G as any).refillGoodsStarted === true;
    const resultCleared = G.autoPhaseResult === null;
    debug.log('[refillGoodsDetroitPhase] ★ endIf check: started =', started, ', resultCleared =', resultCleared);
    return started && resultCleared;
  },

  onEnd: ({ G }: { G: DetroitGameState }) => {
    debug.log('[refillGoodsDetroitPhase] ★★★ Phase 9 ending ★★★');

    // フラグのリセット
    if ((G as any).refillGoodsStarted) {
      (G as any).refillGoodsStarted = false;
    }
    if ((G as any).lastDiceRolls) {
      (G as any).lastDiceRolls = undefined;
    }
    if ((G as any).lastGoodsPlacement) {
      (G as any).lastGoodsPlacement = undefined;
    }
  },

  next: 'advanceTurn'
};

/**
 * Detroit用ダイスロール商品補充
 * - ライトサイド: ダイス2個 → 各ダイス目の列の商品を都市へ
 * - ダークサイド: ダイス2個 → 各ダイス目の列の商品を都市へ
 */
function executeDiceRollRefillDetroit(
  G: DetroitGameState,
  random: any
): { diceRolls: { light: [number, number]; dark: [number, number] }; placements: GoodsPlacementInfo[] } {
  // ライトサイド用とダークサイド用で各2個ずつダイスを振る
  const lightDice1 = random.Die(6);
  const lightDice2 = random.Die(6);
  const darkDice1 = random.Die(6);
  const darkDice2 = random.Die(6);
  const diceRolls = {
    light: [lightDice1, lightDice2] as [number, number],
    dark: [darkDice1, darkDice2] as [number, number],
  };

  debug.log(`[executeDiceRollRefillDetroit] Light dice: ${lightDice1}, ${lightDice2}, Dark dice: ${darkDice1}, ${darkDice2}`);

  const placements: GoodsPlacementInfo[] = [];

  // ライトサイドの補充（2回）
  placeGoodsFromDisplay(G, 'light', lightDice1, placements);
  placeGoodsFromDisplay(G, 'light', lightDice2, placements);

  // ダークサイドの補充（2回）
  placeGoodsFromDisplay(G, 'dark', darkDice1, placements);
  placeGoodsFromDisplay(G, 'dark', darkDice2, placements);

  debug.log(`[executeDiceRollRefillDetroit] Placements: ${JSON.stringify(placements)}`);

  return { diceRolls, placements };
}
